import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { ContentType } from "../shared/contentTypes";
import { RatingDataModel, RatingSyncDataModel, SetRatingModel } from "../shared/models/rating.model";
import Utils from "../shared/utils";
import { CoreLogService } from "./log.service";
import { CoreCOMService } from "./com.service";
import { CircularInjectionHelper } from "./circularInjectionHelper";
import { IObjectObserver } from "./IObjectObserver";
import { ObjectObserverFactory } from "./objectObserverFactory";

@Injectable({
  providedIn: "root"
})
export class RatingService {

  public static RatingToStars(rating: number, max_stars: number = 5): number {
    if (max_stars === 0) {
      throw new Error("max_stars is null");
    }
    return Math.floor(rating / (100 / max_stars));
  }

  public static StarsToRating(stars: number, max_stars: number = 5): number {
    if (max_stars === 0) {
      throw new Error("max_stars is null");
    }
    if (stars > max_stars) {
      console.warn(`stars > max_stars (${stars} > ${max_stars}) > fallback stars=max_stars`);
      stars = max_stars;
    }
    return Math.floor(stars * (100 / max_stars));
  }

  private _ratingObsServ: IObjectObserver<RatingDataModel>;

  constructor(private _coreLogService: CoreLogService,
    private _coreCOMService: CoreCOMService,
    private _objObsInjectHelper: CircularInjectionHelper) {
    const me = this;
    me._ratingObsServ = ObjectObserverFactory.GetObjectObserverByInjectionHelper<RatingDataModel>(ContentType.Rating, me._objObsInjectHelper);
  }

  public get ObserveChanges(): Observable<(string | number)[]> {
    return this._ratingObsServ.ObserveChanges;
  }

  public SetRating(message: string, rating: number, contentType: ContentType, contentID: number): Promise<void> {
    const me = this;
    return new Promise<void>((resolve, reject) => {
      try {
        if (me._coreCOMService.environment.networkInformation.isOnline) {
          me.SendRatingToServer(message, rating, contentType, contentID)
            .then((SyncDataModel) => {
              // vorab speichern, so wird etwas zeit eingesparrt
              me._ratingObsServ.SetObjects(SyncDataModel.ratings)
                .then(resolve)
                .catch((error) => {
                  me._coreLogService.error("RatingService.SetRating", "SetObjects", error);
                  // try fallback and save it local
                  me.FallbackSetRatingLocal(message, rating, contentType, contentID)
                    .then(resolve)
                    .catch(reject);
                });
            })
            .catch((error) => { me._coreLogService.error("RatingService.SetRating", "RequestService", error); reject(); });
        } else {
          me.FallbackSetRatingLocal(message, rating, contentType, contentID)
            .then(resolve)
            .catch(reject);
        }
      } catch (error) {
        me._coreLogService.error("RatingService.SetRating", "try catch", error);
        reject();
      }
    });
  }

  private FallbackSetRatingLocal(message: string, rating: number, contentType: ContentType, contentID: number): Promise<void> {
    const me = this;
    return new Promise<void>((resolve, reject) => {
      try {
        if (me._coreCOMService.environment.client.isMobile) {
          // setze es vorerst im storage mit Flag zum synchronesieren
          me._ratingObsServ.GetObject(contentType + "|" + contentID)
            .then((model) => {
              if (Utils.IsNull(model)) {
                model = new RatingDataModel();
                model.content_type = contentType;
                model.content_id = contentID;
              }
              model.message = message;
              model.rating = rating;
              model.DM_iPE = 1; // markiere zur synchronisation
              me._ratingObsServ.SetObject(model)
                .then(resolve)
                .catch((error) => { me._coreLogService.error("RatingService.FallbackSetRatingLocal", "SetObjects", error); reject(); });
            }).catch((error) => { me._coreLogService.error("RatingService.FallbackSetRatingLocal", "GetObject", error); reject(); });
        } else {
          // browser can only work online
          reject("browser need internet");
        }
      } catch (error) {
        me._coreLogService.error("RatingService.FallbackSetRatingLocal", "try catch", error);
        reject();
      }
    });
  }

  private SendRatingToServer(message: string, rating: number, contentType: ContentType, contentID: number): Promise<RatingSyncDataModel> {
    const me = this;
    return new Promise<RatingSyncDataModel>((resolve, reject) => {
      try {
        const RequestData = new SetRatingModel();
        RequestData.content_type = contentType;
        RequestData.content_id = contentID;
        RequestData.message = message;
        RequestData.rating = rating;
        const FunctionName = "rating/SetRating";
        const SyncDataModelConstructor = RatingSyncDataModel;
        me._objObsInjectHelper.yourAppServer.Api.RequestService(SyncDataModelConstructor, FunctionName, RequestData)
          .then((response) => {
            if (response.OK) {
              me._coreLogService.debug("RatingService.SendRatingToServer", "RequestService (response OK)", response);
              resolve(response.Data);
            } else {
              me._coreLogService.error("RatingService.SendRatingToServer", "RequestService (response not OK)", response);
              reject(response.HttpResponse.codeText);
            }
          })
          .catch((error) => { me._coreLogService.error("RatingService.SendRatingToServer", "RequestService", error); reject(); });
      } catch (error) {
        me._coreLogService.error("RatingService.SendRatingToServer", "try catch", error);
        reject();
      }
    });
  }

  public GetRatingStars(contentType: ContentType, contentID: number): Promise<number> {
    const me = this;
    return new Promise<number>((resolve, reject) => {
      me._ratingObsServ.GetObject(contentType + "|" + contentID)
        .then((rating) => {
          resolve(!Utils.IsNull(rating) ? RatingService.RatingToStars(rating.rating) : 0); // Ratung ist in Prozent, wird auf 5 Sterne verteilt.
        })
        .catch((error) => { me._coreLogService.error("RatingService.HasRating", "GetObject", error); reject(); });
    });
  }

  public GetRating(contentType: ContentType, contentID: number): Promise<RatingDataModel> {
    const me = this;
    return new Promise<RatingDataModel>((resolve, reject) => {
      me._ratingObsServ.GetObject(contentType + "|" + contentID)
        .then(resolve)
        .catch((error) => { me._coreLogService.error("RatingService.HasRating", "GetObject", error); reject(); });
    });
  }

}
